/**
 * How long a hangar-screen link has left (suite rule: every access link expires and can be revoked; a
 * hangar-screen link lasts a year, with a visible renewal reminder). The same words are used on the
 * screen itself and in the owner's list of links.
 */

const DAY_MS = 86_400_000

/** Reminders start this long before a link ends. */
export const RENEWAL_WARN_DAYS = 30

export function daysLeft(expiresAt: number, now: number): number {
  return Math.ceil((expiresAt - now) / DAY_MS)
}

/** "27 Oct 2026", in the site's timezone. */
export function expiryDate(expiresAt: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone, day: 'numeric', month: 'short', year: 'numeric' }).format(expiresAt)
}

/** True once a link is inside the reminder window (or past its end). */
export function renewalDue(expiresAt: number | null | undefined, now: number): boolean {
  return typeof expiresAt === 'number' && daysLeft(expiresAt, now) <= RENEWAL_WARN_DAYS
}

/** The line on the hangar screen: null until the link is inside the reminder window. */
export function linkReminder(expiresAt: number | null | undefined, now: number, timeZone: string): string | null {
  if (typeof expiresAt !== 'number' || !renewalDue(expiresAt, now)) return null
  const left = daysLeft(expiresAt, now)
  if (left <= 0) return `Screen link expired ${expiryDate(expiresAt, timeZone)}`
  return `Screen link expires ${expiryDate(expiresAt, timeZone)} · ${left} ${left === 1 ? 'day' : 'days'} left`
}

/** One link's expiry in the owner's list. */
export function expiryText(expiresAt: number | null | undefined, now: number, timeZone: string): string {
  if (typeof expiresAt !== 'number') return ''
  const left = daysLeft(expiresAt, now)
  if (left <= 0) return `expired ${expiryDate(expiresAt, timeZone)}`
  if (left <= RENEWAL_WARN_DAYS) return `expires in ${left} ${left === 1 ? 'day' : 'days'}`
  return `expires ${expiryDate(expiresAt, timeZone)}`
}
